/**
 * Dashboard preferences, reminders, and macro history – single source.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  type DashboardPreferences,
  type DashboardReminder,
  type DayMacroSnapshot,
  REMINDER_PRESETS,
} from './types';
import { scheduleReminderNotifications } from './reminderNotifications';

const PREFS_KEY = 'dashboard_preferences';
const REMINDERS_KEY = 'dashboard_reminders';
const MACRO_DAY_PREFIX = 'dashboard_macro_';

/** Legacy dev preset — strip from persisted storage. */
function is_legacy_test_reminder(r: DashboardReminder): boolean {
  return r.interval_minutes === 1 && /test\s*\(\s*1\s*min\s*\)/i.test(r.label.trim());
}

function sanitize_persisted_reminders(list: DashboardReminder[]): DashboardReminder[] {
  return list.filter(r => !is_legacy_test_reminder(r));
}

const default_prefs: DashboardPreferences = {
  compact_view: false,
  show_calorie_goal: true,
};

function getDayKey(date: string): string {
  return `${MACRO_DAY_PREFIX}${date}`;
}

export function useDashboardPreferences() {
  const [prefs, set_prefs] = useState<DashboardPreferences>(default_prefs);
  const [reminders, set_reminders] = useState<DashboardReminder[]>([]);
  const [loaded, set_loaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([AsyncStorage.getItem(PREFS_KEY), AsyncStorage.getItem(REMINDERS_KEY)])
      .then(([prefs_str, rem_str]) => {
        if (!mounted) {return;}
        if (prefs_str) {
          try {
            const parsed = JSON.parse(prefs_str) as Partial<DashboardPreferences>;
            set_prefs(p => ({ ...default_prefs, ...p, ...parsed }));
          } catch {}
        }
        if (rem_str) {
          try {
            const parsed = JSON.parse(rem_str) as DashboardReminder[];
            const cleaned = sanitize_persisted_reminders(Array.isArray(parsed) ? parsed : []);
            set_reminders(cleaned);
            if (cleaned.length !== (Array.isArray(parsed) ? parsed.length : 0)) {
              AsyncStorage.setItem(REMINDERS_KEY, JSON.stringify(cleaned));
            }
          } catch {}
        }
        set_loaded(true);
      })
      .catch(() => set_loaded(true));
    return () => { mounted = false; };
  }, []);

  const persist_prefs = useCallback((next: DashboardPreferences) => {
    set_prefs(next);
    AsyncStorage.setItem(PREFS_KEY, JSON.stringify(next));
  }, []);

  const persist_reminders = useCallback((next: DashboardReminder[]) => {
    set_reminders(next);
    void (async () => {
      await AsyncStorage.setItem(REMINDERS_KEY, JSON.stringify(next));
      await scheduleReminderNotifications(next);
    })();
  }, []);

  const set_compact_view = useCallback(
    (v: boolean) => persist_prefs({ ...prefs, compact_view: v }),
    [prefs, persist_prefs],
  );
  const set_show_calorie_goal = useCallback(
    (v: boolean) => persist_prefs({ ...prefs, show_calorie_goal: v }),
    [prefs, persist_prefs],
  );

  const add_reminder = useCallback(
    (preset_id: string, custom_interval_minutes?: number, label?: string) => {
      const preset = REMINDER_PRESETS.find(p => p.id === preset_id);
      const interval = custom_interval_minutes ?? preset?.interval_minutes ?? 120;
      const new_one: DashboardReminder = {
        id: `rem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        label: label?.trim() || 'Reminder',
        interval_minutes: interval,
        icon: preset?.icon ?? 'bell',
        enabled: true,
        last_trigger_at: null,
        created_at: Date.now(),
      };
      persist_reminders([...reminders, new_one]);
      return new_one;
    },
    [reminders, persist_reminders],
  );

  const add_custom_reminder = useCallback(
    (opts: { label: string; icon: string; icon_color?: string | null; interval_minutes: number }) => {
      const new_one: DashboardReminder = {
        id: `rem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        label: opts.label.trim() || 'Reminder',
        interval_minutes: opts.interval_minutes,
        icon: opts.icon,
        icon_color: opts.icon_color ?? null,
        enabled: true,
        last_trigger_at: null,
        created_at: Date.now(),
      };
      persist_reminders([...reminders, new_one]);
      return new_one;
    },
    [reminders, persist_reminders],
  );

  const remove_reminder = useCallback(
    (id: string) => persist_reminders(reminders.filter(r => r.id !== id)),
    [reminders, persist_reminders],
  );
  const toggle_reminder = useCallback(
    (id: string, enabled: boolean) =>
      persist_reminders(reminders.map(r => (r.id === id ? { ...r, enabled } : r))),
    [reminders, persist_reminders],
  );
  const mark_reminder_triggered = useCallback(
    (id: string) =>
      persist_reminders(reminders.map(r => (r.id === id ? { ...r, last_trigger_at: Date.now() } : r))),
    [reminders, persist_reminders],
  );

  return useMemo(
    () => ({
      prefs,
      reminders,
      loaded,
      set_compact_view,
      set_show_calorie_goal,
      add_reminder,
      add_custom_reminder,
      remove_reminder,
      toggle_reminder,
      mark_reminder_triggered,
    }),
    [
      prefs,
      reminders,
      loaded,
      set_compact_view,
      set_show_calorie_goal,
      add_reminder,
      add_custom_reminder,
      remove_reminder,
      toggle_reminder,
      mark_reminder_triggered,
    ],
  );
}

export function useMacroHistory() {
  const [history, set_history] = useState<DayMacroSnapshot[]>([]);
  const [loaded, set_loaded] = useState(false);

  const load_last_7_days = useCallback(async () => {
    const today = new Date();
    const keys: string[] = [];
    for (let i = -7; i <= 7; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      keys.push(getDayKey(d.toISOString().slice(0, 10)));
    }
    const pairs = await AsyncStorage.multiGet(keys);
    const out: DayMacroSnapshot[] = [];
    for (let i = 0; i < pairs.length; i++) {
      const date = keys[i]!.replace(MACRO_DAY_PREFIX, '');
      const raw = pairs[i]![1];
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as DayMacroSnapshot;
          out.push({ ...parsed, date });
        } catch {
          out.push({ date, calories: 0, protein: 0, carbs: 0, fats: 0 });
        }
      } else {
        out.push({ date, calories: 0, protein: 0, carbs: 0, fats: 0 });
      }
    }
    set_history(out);
    set_loaded(true);
  }, []);

  useEffect(() => {
    load_last_7_days();
  }, [load_last_7_days]);

  const save_day = useCallback(
    async (date: string, calories: number, protein: number, carbs: number, fats: number) => {
      const key = getDayKey(date);
      const snapshot: DayMacroSnapshot = { date, calories, protein, carbs, fats };
      await AsyncStorage.setItem(key, JSON.stringify(snapshot));
      await load_last_7_days();
    },
    [load_last_7_days],
  );

  return useMemo(
    () => ({ history, loaded, save_day, refetch: load_last_7_days }),
    [history, loaded, save_day, load_last_7_days],
  );
}
